/** tm/components/GovProgramForm.tsx — 정부지원사업 등록/수정 폼 (GovPage.tsx 에서 분리)
 *
 *  상태(form)와 저장 로직은 부모(GovPage)가 갖고, 이 컴포넌트는 필드 렌더링만 한다.
 *  자주 쓰는 항목(사업명·마감일·지역·업종·기업규모·창업여부·대표나이·분류)은 항상 보이고,
 *  fphub 카탈로그와 같은 세부 조건(연매출·상시근로자·업력·특허/상표)은 「매칭 조건 더보기」로
 *  접어 60대 사용자도 화면이 복잡해 보이지 않게 한다.
 *
 *  사업명 두 벌 (2026-10-01 지도사 확정):
 *   · 사업명(FP 전송용) name   — 「[대구 동구] 이노비즈·메인비즈·벤처 인증취득 수수료 지원」 FP 문자에 쓴다
 *   · 리포트 사업명 report_name — fphub 정부지원 리포트에 보이는 공식 사업명만 「초기창업패키지」
 *   지난 사업(마감 < 오늘)은 FP 전송용 이름이 필요 없다 — 리포트 사업명을 주 칸으로, FP 이름은 접어 두고 비우면 리포트 사업명으로 저장.
 */
import { useState } from 'react';
import DateInput from './DateInput';
import type { GovProgram, GovTrack, GovConditions, GovTrackUnit } from '../types';
import { C, R, T, Field, IMEText, REGION_CITIES } from './ui';
import { today } from '../hooks';
import * as api from '../api';
import { ConditionsRow, conditionsCount, GovTracksSection, GovPreviewLine } from './GovProgramExtras';
import GovTargetPicker from './GovTargetChips';
import GovSubPicker from './GovSubPicker';
import { isObj, UNIT_OPTIONS } from './govRuleText';

import { GovCategoryPicker, GOV_CATEGORIES as TAX_CATEGORIES, GOV_ARTS as TAX_ARTS, GOV_SPECIAL, MFG_SUB_NAMES, RND_DEFAULT_BIZ, GOV_PARENTS, isSubKey, subLabel, mfgKeys, expandSubKeys, OLD_SUB_KEYS, tidyConditions } from './GovTaxonomy';
/* 분류 체계는 GovTaxonomy.tsx 한 벌 (지도사 2026-09-25) — '전체' 는 목록 필터용 */
export const GOV_CATEGORIES = ['전체', ...TAX_CATEGORIES];
// 전용 분야 매칭: 공고에 지정 시 같은 분야 고객에게만 추천 (일반 분류와 작동 방식이 다름 — 게이트)
export const GOV_ARTS = TAX_ARTS;
export const REGIONS = ['서울', '부산', '대구', '인천', '광주', '대전', '울산', '세종', '경기', '강원', '충북', '충남', '전북', '전남', '경북', '경남', '제주'];
export const BIZ_TYPES = GOV_PARENTS; // 2026-10-03 숙박/음식점업 → 둘로 · 2026-10-05g 농림축수산업 (엔진 PARENTS 와 같은 한 벌)
// 엔진 scaleCode() 가 라벨을 인식한다(소상공인⊂소기업⊂중소기업⊂중소·중견) — 「무관」 포함 4단계(감사 A-5)
const SCALE_OPTIONS = ['소상공인', '소기업', '중소기업', '중견기업'];
const OP_OPTIONS: { v: string; label: string }[] = [{ v: '', label: '무관' }, { v: '<', label: '미만' }, { v: '<=', label: '이하' }, { v: '>=', label: '이상' }, { v: '>', label: '초과' }];

export interface GovProgramFormState {
  name: string; report_name: string; deadline: string; first_come: boolean;
  apply_start: string; recur_yearly: boolean;
  /** 모집 달(2026-10-14) — null = 자동(공고 회차에서) · monthsDirty = 지도사가 고쳤다(저장 때만 보낸다) */
  apply_months: number[] | null; monthsDirty: boolean;
  amount: string; amount_max_manwon: string;
  agency: string; link: string;
  /** 리포트 짧은 이름 · 대표 주관기관 (2026-10-07) — briefDirty = 지도사가 고쳤다(저장 때만 보낸다 · 서버가 manual 로 둬 AI 가 덮지 않음) */
  brief_name: string; lead_agency: string; briefDirty: boolean;
  /** 정식 사업명(고유명 · canon_name · 2026-10-17) — 리포트 로드맵 1줄 이름 · briefDirty 와 함께 보낸다 */
  canon_name: string;
  /** 업종 칸을 지도사가 손으로 고쳤다 (2026-10-07f) — 저장 때 conditions.biz_by = 'manual' 로 보내 AI 다시 읽기가 업종을 바꾸지 않게 */
  bizDirty: boolean;
  regions: string[]; region_details: string[]; biz_types: string[];
  company_scale: string; startup_filter: string;
  ceo_age_max: string; ceo_age_min: string;
  sales_op: string; sales_eok: string;
  emp_op: string; emp_n: string;
  yrs_min: string; yrs_max: string;
  want_patent: boolean; want_tm: boolean; exclude_micro: boolean;
  category: string;
  /** 세부 유형 (2026-10-05) — null = 이 폼이 모르는 값(저장 바디에 넣지 않아 서버의 기존 값 유지). [] = 비움 */
  tracks: GovTrack[] | null;
  /** 공고 공통 확장 조건 — null 이면 바디에 넣지 않는다. meta(자부담·지원비율 등)는 화면에 없고 객체째 보존 */
  conditions: GovConditions | null;
}

export const emptyGovProgramForm = (): GovProgramFormState => ({
  name: '', report_name: '', deadline: '', first_come: false, apply_start: '', recur_yearly: true, // 기본 매년 반복 (2026-10-03)
  apply_months: null, monthsDirty: false,
  amount: '', amount_max_manwon: '', agency: '', link: '', brief_name: '', lead_agency: '', canon_name: '', briefDirty: false, bizDirty: false,
  regions: [], region_details: [], biz_types: [], company_scale: '', startup_filter: '',
  ceo_age_max: '', ceo_age_min: '', sales_op: '', sales_eok: '', emp_op: '', emp_n: '',
  yrs_min: '', yrs_max: '', want_patent: false, want_tm: false, exclude_micro: false, category: '',
  tracks: null, conditions: null,
});

/** JSON 문자열도 받는다 (깨진 값이면 undefined) */
function jsonish(v: unknown): unknown {
  if (typeof v !== 'string') return v;
  if (!v.trim()) return undefined;
  try { return JSON.parse(v) as unknown; } catch { return undefined; }
}
/** 행의 tracks → 폼 값. 없거나 깨졌으면 null(보내지 않음 — 서버 값 유지). 원소의 meta·날짜 등 모르는 칸은 그대로 둔다 */
export function tracksFromRow(raw: unknown): GovTrack[] | null {
  const a = jsonish(raw);
  if (!Array.isArray(a)) return null;
  const units = UNIT_OPTIONS.map(u => u.v as string);
  const seen = new Set<string>();
  return a.filter(isObj).map((t, i) => {
    let id = typeof t.id === 'string' && /^[A-Za-z0-9_-]{1,24}$/.test(t.id) ? t.id : '';
    if (!id || seen.has(id)) id = `r${i}${Math.random().toString(36).slice(2, 7)}`;
    seen.add(id);
    const man = t.amount_max_manwon == null || t.amount_max_manwon === '' ? null : Number(t.amount_max_manwon);
    return {
      ...(t as Partial<GovTrack>),
      id,
      name: t.name == null ? '' : String(t.name),
      amount: t.amount == null ? '' : String(t.amount),
      amount_max_manwon: man != null && Number.isFinite(man) ? man : null,
      unit: (typeof t.unit === 'string' && units.includes(t.unit) ? t.unit : '') as GovTrackUnit,
      rules: isObj(t.rules) ? t.rules : {},
    };
  });
}
/** 행의 conditions → 폼 값. 없거나 깨졌으면 null */
export function conditionsFromRow(raw: unknown): GovConditions | null {
  const c = jsonish(raw);
  return isObj(c) ? (c as GovConditions) : null;
}

/** YYYY-MM-DD 문자열의 연도만 옮긴다(복제 시 +1) — 형식이 아니면 빈 문자열 */
function shiftDate(d: string | null | undefined, addYears: number): string {
  if (!d) return '';
  const m = String(d).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  return `${Number(m[1]) + addYears}-${m[2]}-${m[3]}`;
}

/** 공고 행(GovProgram) → 폼 상태. opts.shiftYear 를 주면 마감일·신청시작일을 +1년 해 「전년도 공고 복제」에 쓴다 */
export function govProgramFormFromRow(g: GovProgram, opts?: { shiftYear?: boolean }): GovProgramFormState {
  const add = opts?.shiftYear ? 1 : 0;
  const regions = (g.region || '').split(',').map(s => s.trim()).filter(Boolean);
  const region_details = (g.region_detail || '').split(',').map(s => s.trim()).filter(Boolean);
  const bizRaw: string[] = Array.isArray(g.target_biz_types) ? g.target_biz_types
    : (typeof g.target_biz_types === 'string' ? (() => { try { return JSON.parse(g.target_biz_types as unknown as string); } catch { return []; } })() : []);
  // 옛 「제조업 세부 분야」(업종 칸에 반도체·전자 …) → 세부업종 키 (2026-10-05g — 엔진도 같은 뜻으로 읽는다)
  const legacyMfg = bizRaw.filter(b => MFG_SUB_NAMES.includes(b));
  const biz_types = bizRaw.filter(b => !MFG_SUB_NAMES.includes(b)).concat(legacyMfg.length && !bizRaw.includes('제조업') ? ['제조업'] : []);
  const cond000 = conditionsFromRow(g.conditions);
  // 세부업종 키뿐인 「또는」 묶음은 세부업종 칩으로(업종 키 · 무관 키 섞여도 — 켜진 칩은 전부 하나라도) · 자동 무관 › S 와 같은 이름의 업종 › S 가 있으면 업종 › S 만 (엔진 tidyTargets · 2026-10-05n · 05q)
  const cond00: GovConditions | null = cond000 ? tidyConditions(cond000, bizRaw.filter(b => !MFG_SUB_NAMES.includes(b)).concat(bizRaw.some(b => MFG_SUB_NAMES.includes(b)) ? ['제조업'] : [])) : null;
  // 저장된 옛 키(「제조업›바이오·제약·의료기기」 …)는 새 키 둘로 읽는다 — 양식에서 저장해도 사라지지 않게 (엔진 OLD_KEYS · 2026-10-05m)
  const oldSubs = cond00 && Array.isArray(cond00.subs) ? cond00.subs : [], oldOff = cond00 && Array.isArray(cond00.sec_off) ? cond00.sec_off : [];
  const hasOld = [...oldSubs, ...oldOff].some(k => !!OLD_SUB_KEYS[k]);
  const cond0: GovConditions | null = cond00 && hasOld
    ? { ...cond00, ...(oldSubs.length ? { subs: expandSubKeys(oldSubs) } : {}), ...(oldOff.length ? { sec_off: expandSubKeys(oldOff) } : {}) }
    : cond00;
  const conditions: GovConditions | null = legacyMfg.length
    ? { ...(cond0 || {}), subs: Array.from(new Set([...((cond0 && Array.isArray(cond0.subs)) ? cond0.subs.filter(isSubKey) : []), ...legacyMfg.flatMap(mfgKeys)])) }
    : cond0;
  const numStr = (v: number | null | undefined) => (v != null ? String(v) : '');
  return {
    name: g.name, report_name: g.report_name || '', deadline: shiftDate(g.deadline, add), first_come: !!g.first_come,
    apply_start: shiftDate(g.apply_start, add), recur_yearly: !!g.recur_yearly,
    apply_months: monthsOf(g.apply_months), monthsDirty: false,
    amount: g.amount || '', amount_max_manwon: numStr(g.amount_max_manwon),
    agency: g.agency || '', link: g.link || '', brief_name: add ? '' : (g.brief_name || ''), lead_agency: g.lead_agency || '', canon_name: g.canon_name || '', briefDirty: false, bizDirty: false,
    regions, region_details, biz_types,
    company_scale: g.target_company_scale || '', startup_filter: g.target_startup || '',
    ceo_age_max: numStr(g.target_ceo_age_max), ceo_age_min: numStr(g.target_ceo_age_min),
    sales_op: g.target_sales_op || '', sales_eok: numStr(g.target_sales_eok),
    emp_op: g.target_emp_op || '', emp_n: numStr(g.target_emp_n),
    yrs_min: numStr(g.target_yrs_min), yrs_max: numStr(g.target_yrs_max),
    want_patent: !!g.want_patent, want_tm: !!g.want_tm, exclude_micro: !!g.exclude_micro,
    category: g.category || '',
    // 전년도 복제면 트랙 날짜도 +1년 (형식이 아니면 비운다)
    tracks: (() => { const ts = tracksFromRow(g.tracks); return ts && add ? ts.map(t => ({ ...t, apply_start: shiftDate(t.apply_start, add) || null, deadline: shiftDate(t.deadline, add) || null })) : ts; })(),
    conditions,
  };
}

/** 저장 값(JSON 글 · 배열) → 모집 달 배열 | null(자동) */
function monthsOf(v: string | number[] | null | undefined): number[] | null {
  if (v == null || v === '') return null;
  let a: unknown = v;
  if (typeof a === 'string') { try { a = JSON.parse(a); } catch { return null; } }
  return Array.isArray(a) ? a.map(Number).filter(n => Number.isInteger(n) && n >= 1 && n <= 12) : null;
}

/** 지난 사업인가 — 마감일이 있고 오늘보다 앞(선착순·상시는 아님). 서버 programName.isPastDeadline 과 같은 규칙 */
export function isPastGovForm(form: Pick<GovProgramFormState, 'deadline' | 'first_come'>): boolean {
  return !form.first_come && !!form.deadline && form.deadline.slice(0, 10) < today();
}

/** 저장할 수 있나 — FP 사업명이 있거나, 지난 사업이면 리포트 사업명만 있어도 된다 */
export function canSaveGovForm(form: GovProgramFormState): boolean {
  return !!form.name.trim() || (isPastGovForm(form) && !!form.report_name.trim());
}

/** 폼 상태 → 저장 payload (POST/PUT /gov-programs 바디) */
export function govProgramFormToPayload(form: GovProgramFormState): Partial<GovProgram> {
  const tags = form.category ? form.category.split(',').map(s => s.trim()).filter(Boolean) : [];
  const n = (s: string): number | null => (s.trim() !== '' ? Number(s) : null);
  return {
    // 지난 사업에 FP 이름이 비었으면 리포트 사업명으로 (서버도 같은 규칙 — name NOT NULL)
    name: form.name.trim() ? form.name : (isPastGovForm(form) ? form.report_name.trim() : form.name),
    report_name: form.report_name.trim(),
    deadline: form.first_come ? null : (form.deadline || null),
    first_come: form.first_come,
    apply_start: form.apply_start || null,
    recur_yearly: form.recur_yearly,
    ...(form.monthsDirty ? { apply_months: form.apply_months } : {}),   // 고쳤을 때만(null = 자동으로 되돌림)
    agency: form.agency,
    ...(form.briefDirty ? { brief_name: form.brief_name.trim(), lead_agency: form.lead_agency.trim(), canon_name: form.canon_name.trim() } : {}),
    amount: form.amount,
    amount_max_manwon: n(form.amount_max_manwon),
    category: form.category,
    tags,
    region: form.regions.join(','),
    region_detail: form.region_details.join(','),
    link: form.link,
    target_biz_types: form.biz_types,
    target_company_scale: form.company_scale,
    target_startup: form.startup_filter,
    target_ceo_age_max: n(form.ceo_age_max),
    target_ceo_age_min: n(form.ceo_age_min),
    // 연산자를 골라도 값이 비어 있으면 조건 없음으로 취급(반쪽 조건 저장 방지)
    target_sales_op: form.sales_eok.trim() !== '' ? form.sales_op : '',
    target_sales_eok: n(form.sales_eok),
    target_emp_op: form.emp_n.trim() !== '' ? form.emp_op : '',
    target_emp_n: n(form.emp_n),
    target_yrs_min: n(form.yrs_min),
    target_yrs_max: n(form.yrs_max),
    want_patent: form.want_patent,
    want_tm: form.want_tm,
    exclude_micro: form.exclude_micro,
    // 세부 유형·확장 조건 (2026-10-05) — 폼이 아는 값일 때만 (null 이면 키를 빼서 서버의 기존 값 유지). 서버가 빈 트랙을 빼고 정규화한다
    ...(form.tracks ? { tracks: form.tracks } : {}),
    // 업종 칸을 손으로 고쳤으면 biz_by manual (AI 다시 읽기가 업종을 바꾸지 않는다 · 2026-10-07f) — conditions 를 모르는 옛 행이어도 그 키만 보낸다
    ...(form.conditions || form.bizDirty ? { conditions: { ...(form.conditions || {}), ...(form.bizDirty ? { biz_by: 'manual' as const } : {}) } } : {}),
  };
}

/** 등록 양식 pill — 공고 검토함 표의 인라인 편집도 같은 모양을 쓴다 */

export function Pill({ active, included, onClick, children, activeBorder, activeBg, activeColor, title }: {
  active: boolean;
  /** 포함 상태(선택값에 딸려 함께 적용됨) — 활성색의 연한 톤 + 보통 굵기. active 가 우선 */
  included?: boolean;
  onClick: () => void; children: React.ReactNode; activeBorder: string; activeBg: string; activeColor: string; title?: string;
}) {
  const inc = !active && !!included;
  // 6자리 hex 에 알파(80 ≈ 50%)를 붙여 흰 바탕 위 연한 톤으로 — 활성 pill 과 같은 색 계열 유지
  const soft = (hex: string) => (/^#[0-9a-fA-F]{6}$/.test(hex) ? `${hex}80` : hex);
  return (
    <span onClick={onClick} title={title} style={{
      padding: '4px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
      border: `1px solid ${active ? activeBorder : inc ? soft(activeBorder) : C.border}`,
      background: active ? activeBg : inc ? soft(activeBg) : C.white,
      color: active || inc ? activeColor : C.sub,
      fontWeight: active ? 600 : 400,
    }}>{children}</span>
  );
}

const numInput: React.CSSProperties = { width: 76, padding: '6px 9px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 12, outline: 'none' };
const selInput: React.CSSProperties = { padding: '6px 8px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 12, outline: 'none', background: C.white };
const rowLabel: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: C.sub, marginBottom: 5 };

interface Props {
  form: GovProgramFormState;
  setForm: React.Dispatch<React.SetStateAction<GovProgramFormState>>;
  /** 「제목에서 만들기」의 원본 — 공고 검토함은 공고명, 없으면 FP 사업명에서 만든다 */
  sourceTitle?: string;
  /** 등록된 공고를 고칠 때 — 「AI 다시 읽기」(공고문 전체로 하위 업종 · 전용 대상 다시 판정 · 2026-10-05o) 단추를 보인다 */
  programId?: string | null;
}

const nameInput: React.CSSProperties = { width: '100%', height: 38, padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: T.md, outline: 'none', boxSizing: 'border-box' };
const hintText: React.CSSProperties = { fontSize: T.xs, color: '#9CA3AF', marginTop: 4, lineHeight: 1.45 };

/** 리포트 사업명 칸 — 입력 + 「제목에서 만들기」 (서버 규칙) */
function ReportNameField({ value, onChange, source, required }: { value: string; onChange: (v: string) => void; source: string; required?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  async function derive() {
    if (busy || !source.trim()) return;
    setBusy(true); setNote('');
    try {
      const r = await api.govReportNameFrom(source);
      if (r.success && r.data?.report_name) onChange(r.data.report_name);
      else setNote(r.error || '만들지 못했습니다');
    } catch { setNote('만들지 못했습니다'); }
    setBusy(false);
  }
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ fontSize: T.xs, fontWeight: 600, color: C.sub, display: 'block', marginBottom: 3 }}>
        리포트 사업명{required && <span style={{ color: C.red }}> *</span>}
      </label>
      <div style={{ display: 'flex', gap: 6 }}>
        <IMEText value={value} onChange={onChange} placeholder="예: 초기창업패키지" maxLength={200} style={{ ...nameInput, flex: 1, minWidth: 0 }} />
        <button type="button" onClick={derive} disabled={busy || !source.trim()} title={source ? `「${source}」에서 [지역]·연도·금액을 뗍니다 — 무엇을 지원하는지가 남습니다` : '사업명을 먼저 입력하세요'}
          style={{ flexShrink: 0, height: 38, padding: '0 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, color: busy || !source.trim() ? C.muted : C.accent,
            fontSize: T.sm, fontWeight: 600, cursor: busy || !source.trim() ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
          {busy ? '만드는 중…' : '사업명에서 만들기'}
        </button>
      </div>
      <div style={{ ...hintText, color: note ? C.red : hintText.color }}>{note || 'fphub 정부지원 리포트에 표시 — 제목만 보고 무슨 지원인지 알 수 있게(연도·지역·금액·기관 없이)'}</div>
    </div>
  );
}

export default function GovProgramForm({ form, setForm, sourceTitle, programId }: Props) {
  // 사업명으로 알아본 전용 대상 — 저장 전 미리보기(서버 엔진)가 알려 준다 (2026-10-05f)
  const [nameAuto, setNameAuto] = useState<string[]>([]);
  // 「AI 다시 읽기」 — 서버에 저장된 공고의 공고문 전체를 AI 가 읽어 판정 · 결과(저장된 값)로 양식의 하위 업종 · 전용 대상 칸을 바꾼다 (2026-10-05o)
  const [aiNote, setAiNote] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  async function rereadAi() {
    if (!programId || aiBusy) return;
    setAiBusy(true); setAiNote('');
    try {
      const r = await api.aiRereadGovTarget(programId);
      if (!r.success || !r.data) throw new Error(r.error || 'AI 다시 읽기에 실패했습니다');
      if ('background' in r.data) { setAiNote('AI 가 아직 읽는 중 — 잠시 뒤 다시 열면 결과가 보입니다'); return; }
      const c = r.data.conditions || {};
      setForm(p => ({ ...p, conditions: { ...(p.conditions || {}), subs: c.subs, sec: c.sec, sec_off: c.sec_off, sec_auto: c.sec_auto, any: c.any, tech: c.tech, bizx: c.bizx } }));
      const on = [...(c.subs || []), ...(c.sec || [])];
      setAiNote(`AI 판정 — ${on.length ? on.map(k => (isSubKey(k) ? subLabel(k) : k)).join(' · ') : '세부업종 · 전용 대상 없음'} (${{ high: '확신 높음', mid: '확신 중간', low: '확신 낮음' }[r.data.conf] || r.data.conf}${r.data.note ? ` · ${r.data.note}` : ''})`);
    } catch (e) { setAiNote(e instanceof Error ? e.message : 'AI 다시 읽기에 실패했습니다'); }
    finally { setAiBusy(false); }
  }
  const [advOpen, setAdvOpen] = useState<boolean>(() =>
    !!(form.sales_op || form.sales_eok || form.emp_op || form.emp_n || form.yrs_min || form.yrs_max || form.want_patent || form.want_tm || conditionsCount(form.conditions) > 0));
  const past = isPastGovForm(form);
  const condN = conditionsCount(form.conditions);
  // 지난 사업: FP 이름이 리포트 사업명과 같았으면(또는 비었으면) 펼치지 않는다 — 따로 고친 FP 이름이 있을 때만 펼쳐 둔다
  const [fpOpen, setFpOpen] = useState<boolean>(() => !!form.name.trim() && form.name.trim() !== form.report_name.trim());
  const upd = <K extends keyof GovProgramFormState>(k: K, v: GovProgramFormState[K]) => setForm(p => ({ ...p, [k]: v }));
  // 리포트 사업명 고치기 — 지난 사업은 FP 이름이 리포트 사업명을 따라간다(같았거나 비었을 때만)
  const setReportName = (v: string) => setForm(p => {
    const linked = isPastGovForm(p) && (!p.name.trim() || p.name.trim() === p.report_name.trim());
    return { ...p, report_name: v, name: linked ? v : p.name };
  });
  // 리포트 사업명은 «무엇을 지원하는지» — FP 사업명에서 [지역]·금액을 뗀 꼴 (2026-10-03 지도사). FP 사업명이 비었으면 공고명에서
  const source = form.name.trim() || (sourceTitle || '').trim();
  // 기업규모: 선택값 = 적용 가능한 최대 규모, 그보다 작은 규모는 모두 포함
  const scaleIdx = SCALE_OPTIONS.indexOf(form.company_scale);
  const scaleHint = scaleIdx < 0 ? ''
    : scaleIdx === 0 ? `${SCALE_OPTIONS[0]}만`
    : `${SCALE_OPTIONS[scaleIdx]}까지 — ${SCALE_OPTIONS.slice(0, scaleIdx).join('·')} 포함`;

  return (
    <>
      {past ? (
        <>
          <ReportNameField value={form.report_name} onChange={setReportName} source={source} required />
          <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 8, background: C.bgAlt, border: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ flex: 1, fontSize: T.xs, color: C.sub, lineHeight: 1.45 }}>
                <b style={{ fontWeight: 600 }}>지난 공고</b> — FP 전송용 이름은 지금 신청 가능한 공고에만 필요합니다
              </span>
              <button type="button" onClick={() => setFpOpen(o => !o)}
                style={{ flexShrink: 0, padding: '2px 8px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.white, color: C.sub, fontSize: T.xs, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {fpOpen ? '▾ FP 사업명 접기' : '▸ FP 사업명 입력'}
              </button>
            </div>
            {fpOpen && (
              <div style={{ marginTop: 8 }}>
                <IMEText value={form.name} onChange={v => upd('name', v)} placeholder={form.report_name || '비우면 리포트 사업명으로 저장'} style={{ ...nameInput, background: C.white }} />
                <div style={hintText}>선택 — 비우면 리포트 사업명으로 저장합니다</div>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <Field label="사업명 (FP 전송용)" value={form.name} required placeholder="[지역] 무엇을 + 금액 + 지원" onChange={v => upd('name', v)} />
          <ReportNameField value={form.report_name} onChange={setReportName} source={source} />
        </>
      )}

      {/* 마감일 · 선착순 · 신청 시작일 · 매년 반복 — 「미리 준비」 컨셉(지도사 2026-09-25) */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 150 }}>
          <div style={rowLabel}>마감일</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 130 }}><DateInput value={form.deadline} onChange={x => upd('deadline', x)} disabled={form.first_come} aria-label="마감일"
              style={{ padding: '8px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13, outline: 'none', opacity: form.first_come ? 0.4 : 1 }} /></div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', flexShrink: 0 }}>
              <input type="checkbox" checked={form.first_come} onChange={e => setForm(p => ({ ...p, first_come: e.target.checked, deadline: e.target.checked ? '' : p.deadline }))} style={{ accentColor: C.accent }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: form.first_come ? C.accent : C.sub }}>선착순</span>
            </label>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 150 }}>
          <div style={rowLabel}>신청 시작일 <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(비우면 상시 안내)</span></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 130 }}><DateInput value={form.apply_start} onChange={x => upd('apply_start', x)} aria-label="신청 시작일"
              style={{ padding: '8px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13, outline: 'none' }} /></div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', flexShrink: 0 }}>
              <input type="checkbox" checked={form.recur_yearly} onChange={e => upd('recur_yearly', e.target.checked)} style={{ accentColor: C.accent }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: form.recur_yearly ? C.accent : C.sub }}>매년 반복</span>
            </label>
          </div>
        </div>
      </div>

      {/* 지원 종료 달 — 내년 예산이 없는 사업(2027 정부안 0원 등)은 그 달까지만 리포트 달력에 · 지나면 싣지 않음 (2026-10-18 지도사 「내년 1월부터는 공란」) */}
      <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ ...rowLabel, marginBottom: 0 }}>지원 종료 <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(이 달까지만 · 비우면 계속)</span></div>
        <input type="month" value={form.conditions?.end_ym || ''} aria-label="지원 종료 달"
          onChange={e => { const v = e.target.value; setForm(p => { const c: GovConditions = { ...(p.conditions || {}) }; if (/^\d{4}-\d{2}$/.test(v)) c.end_ym = v; else delete c.end_ym; return { ...p, conditions: c }; }); }}
          style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${form.conditions?.end_ym ? C.accent : C.border}`, fontSize: 13, outline: 'none', fontFamily: 'inherit', fontVariantNumeric: 'tabular-nums' }} />
        {form.conditions?.end_ym ? <button type="button" onClick={() => setForm(p => { const c: GovConditions = { ...(p.conditions || {}) }; delete c.end_ym; return { ...p, conditions: c }; })}
          style={{ border: 'none', background: 'none', padding: 0, fontSize: 12, color: C.accent, cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit' }}>지우기</button> : null}
      </div>

      {/* 모집 달 — 한 해에 여러 번 모집하면 달마다 체크(2026-10-14 지도사 「달력에 날짜만 체크체크 따로따로」) · 비우면 공고 회차에서 자동 */}
      <div style={{ marginTop: 10 }}>
        <div style={{ ...rowLabel, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span>모집 달 <span style={{ color: '#9CA3AF', fontWeight: 400 }}>{form.apply_months === null ? '(자동 — 수집 공고 회차 달 · 여러 번 모집할 때만 고르세요)' : form.apply_months.length < 2 ? '(두 달 이상 골라야 달력에 따로 표시 · 아니면 신청 기간대로)' : '(달력에 이 달들만 따로 표시)'}</span></span>
          {form.apply_months !== null && <button type="button" onClick={() => setForm(p => ({ ...p, apply_months: null, monthsDirty: true }))} style={{ border: 'none', background: 'none', padding: 0, fontSize: 12, color: C.accent, cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit' }}>자동으로</button>}
        </div>
        <div role="group" aria-label="모집 달" style={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0,1fr))', gap: 4 }}>
          {Array.from({ length: 12 }, (_, i) => i + 1).map(m => {
            const on = !!form.apply_months && form.apply_months.includes(m);
            return <button key={m} type="button" aria-pressed={on} onClick={() => setForm(p => { const cur = p.apply_months || []; const next = on ? cur.filter(x => x !== m) : [...cur, m].sort((a, b) => a - b); return { ...p, apply_months: next, monthsDirty: true }; })}
              style={{ height: 28, minWidth: 0, padding: 0, borderRadius: 6, border: `1px solid ${on ? C.accent : C.border}`, background: on ? C.accent : '#fff', color: on ? '#fff' : C.sub, fontSize: 12, fontWeight: on ? 700 : 500, cursor: 'pointer', fontFamily: 'inherit', fontVariantNumeric: 'tabular-nums', overflow: 'hidden' }} aria-label={`${m}월`}>{m}</button>;
          })}
        </div>
      </div>

      {/* 지원금액 — 자유 문자열(표기용) + 숫자(만원, 정렬·집계용) */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
        <div style={{ flex: 1, minWidth: 150 }}><Field label="지원금액" value={form.amount} placeholder="최대 1억원" onChange={v => upd('amount', v)} /></div>
        <div style={{ flex: 1, minWidth: 150 }}>
          <div style={rowLabel}>지원금액(숫자) <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(만원)</span></div>
          <input type="number" min={0} value={form.amount_max_manwon} onChange={e => upd('amount_max_manwon', e.target.value)} placeholder="예: 10000"
            style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
        </div>
      </div>
      <Field label="수행기관" value={form.agency} onChange={v => upd('agency', v)} />
      {/* 정식 사업명(고유명 · 2026-10-17 지도사 「고유명을 안 적어서 창업지원 1억 · 초기창업지원 1억 … 헷갈린다」) — 리포트 로드맵 1줄 · 짧은 이름은 2줄 금액 뒤 */}
      <Field label="정식 사업명 (고유명 · 리포트 첫 줄)" value={form.canon_name} placeholder="예: 강한 소상공인 성장지원사업 · 초기창업패키지" onChange={v => setForm(p => ({ ...p, canon_name: v.slice(0, 100), briefDirty: true }))} />
      {/* 리포트 짧은 이름 · 대표 주관기관 (2026-10-07) — 비워 두면 AI 가 채운다 · 고치면 그대로 둔다 */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 180 }}>
          <Field label="리포트 짧은 이름 (지원 내용 · 20자)" value={form.brief_name} placeholder="예: 공정 자동화설비 구축" onChange={v => setForm(p => ({ ...p, brief_name: v.slice(0, 20), briefDirty: true }))} />
        </div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <Field label="대표 주관기관 (비우면 AI)" value={form.lead_agency} placeholder="예: 고용노동부" onChange={v => setForm(p => ({ ...p, lead_agency: v.slice(0, 100), briefDirty: true }))} />
        </div>
      </div>

      <div style={{ height: 1, background: C.border, margin: '12px 0' }} />

      {/* 지역 */}
      <div style={{ marginBottom: 12 }}>
        <div style={rowLabel}>지역 <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(복수 선택)</span></div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Pill active={form.regions.length === 0} onClick={() => setForm(p => ({ ...p, regions: [] }))} activeBorder="#9CA3AF" activeBg="#F3F4F6" activeColor="#374151">무관</Pill>
          {REGIONS.map(r => {
            const active = form.regions.includes(r);
            return <Pill key={r} active={active} activeBorder="#FAC775" activeBg="#FAEEDA" activeColor="#633806" onClick={() => setForm(p => {
              if (active) {
                const citiesOfR = REGION_CITIES[r] || [];
                return { ...p, regions: p.regions.filter(x => x !== r), region_details: p.region_details.filter(c => !citiesOfR.includes(c)) };
              }
              return { ...p, regions: [...p.regions, r] };
            })}>{r}</Pill>;
          })}
        </div>
        {form.regions.filter(r => REGION_CITIES[r]).map(r => (
          <div key={r} style={{ marginTop: 6, padding: '8px 10px', borderRadius: 8, background: C.white, border: '1px solid rgba(250,199,117,0.27)' }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: '#854F0B', marginBottom: 5 }}>▾ {r} 시/군/구 <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(선택 안하면 {r} 전체)</span></div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {REGION_CITIES[r].map(city => {
                const cActive = form.region_details.includes(city);
                return <Pill key={city} active={cActive} activeBorder="#FAC775" activeBg="#FAEEDA" activeColor="#633806"
                  onClick={() => setForm(p => ({ ...p, region_details: cActive ? p.region_details.filter(x => x !== city) : [...p.region_details, city] }))}>{city}</Pill>;
              })}
            </div>
          </div>
        ))}
      </div>

      {/* 업종 › 세부업종 (2026-10-05g · 05q) — 고른 업종마다 세부업종 상자 · 안 고르면 그 업종 전체 · 「무관」 › 세부업종 = 업종 상관없이 · 켜진 칩은 전부 하나라도 */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ ...rowLabel, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>업종 <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(복수 선택 · 하나라도 해당하면)</span></span>
          {programId && (
            <button type="button" disabled={aiBusy} onClick={() => void rereadAi()} title="저장된 공고문 전체를 AI 가 다시 읽고 세부업종 · 전용 대상을 판정합니다 — 결과가 아래 칸에 들어오고, 저장해야 반영됩니다"
              style={{ marginLeft: 'auto', padding: '2px 8px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.white, color: C.sub, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', opacity: aiBusy ? 0.6 : 1 }}>
              {aiBusy ? 'AI 읽는 중…' : 'AI 다시 읽기'}
            </button>
          )}
        </div>
        {aiNote && <div style={{ fontSize: 11, color: C.sub, margin: '-2px 0 6px', lineHeight: 1.45 }}>{aiNote}</div>}
        <GovSubPicker biz={form.biz_types} onBiz={next => setForm(p => ({ ...p, biz_types: next, bizDirty: true }))} conditions={form.conditions} onConditions={c => upd('conditions', c)} nameAuto={nameAuto}
          emptyHint={form.biz_types.length === 0 && form.conditions?.tech === true && !(form.conditions?.subs || []).length ? (
            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 5, lineHeight: 1.45 }}>기술 기반 창업 — 업종을 비워 두면 {RND_DEFAULT_BIZ.join('·')} 업체에만 추천합니다</div>
          ) : form.biz_types.length === 0 && !(form.conditions?.subs || []).length && form.category.split(',').map(c => c.trim()).includes('R&D') ? (
            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 5, lineHeight: 1.45 }}>R&D 공고 — 업종을 비워 두면 {RND_DEFAULT_BIZ.join('·')} 업체에만 추천합니다</div>
          ) : null} />
      </div>

      {/* 기업규모 — 소상공인⊂소기업⊂중소기업⊂중견기업 (엔진 scaleCode 가 인식, 감사 A-5) */}
      <div style={{ marginBottom: 12 }}>
        <div style={rowLabel}>기업규모</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Pill active={!form.company_scale} onClick={() => upd('company_scale', '')} activeBorder="#9CA3AF" activeBg="#F3F4F6" activeColor="#374151">무관</Pill>
          {SCALE_OPTIONS.map((opt, i) => {
            const included = scaleIdx > i; // 선택값보다 작은 규모 = 함께 포함
            return (
              <Pill key={opt} active={form.company_scale === opt} included={included} onClick={() => upd('company_scale', opt)}
                title={included ? `${form.company_scale} 선택에 포함됨 — 누르면 ${opt}까지로 좁힙니다` : undefined}
                activeBorder="#85B7EB" activeBg="#E6F1FB" activeColor="#0C447C">{opt}</Pill>
            );
          })}
        </div>
        {scaleHint && <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 5, lineHeight: 1.45 }}>{scaleHint}</div>}
        {/* 소상공인 제외 (2026-10-04) — 중진공 정책자금처럼 «중소기업이되 소상공인은 안 됨, 제조업 소상공인은 가능» */}
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 12, color: '#374151', cursor: 'pointer' }}
          title="비제조 소상공인(상시 5인 미만 · 건설·운수·광업은 10인 미만)은 매칭에서 뺍니다. 제조업 소상공인은 신청 가능으로 봅니다.">
          <input type="checkbox" checked={form.exclude_micro} onChange={e => upd('exclude_micro', e.target.checked)} style={{ accentColor: C.accent }} />
          소상공인 제외 <span style={{ color: '#9CA3AF' }}>{(() => {   // 예외는 공고 데이터(micro_ok)대로 — 없으면 제조업만(2026-10-26)
            const mo = form.conditions?.micro_ok;
            if (!mo) return '(제조업은 가능)';
            if (mo.all) return '(소상공인도 가능)';
            const ex = [...(mo.biz || []), ...(mo.types || []), ...(mo.check ? [`${mo.check.t} 확인`] : [])];
            return ex.length ? `(예외 ${ex.join(' · ')})` : '(예외 없음)';
          })()}</span>
        </label>
        {/* 지원형태 — 컨설팅 · 교육만 (2026-10-07 지도사 「컨설팅 지원은 다 빼」) — 켜면 어떤 업체에도 추천하지 않는다 */}
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, marginLeft: 14, fontSize: 12, color: '#374151', cursor: 'pointer' }}
          title="기업이 받는 것이 컨설팅 · 진단 · 자문 · 교육뿐(자금 없음)인 공고. 켜면 매칭 · 리포트에서 뺍니다.">
          <input type="checkbox" checked={form.conditions?.form === 'consult'} style={{ accentColor: C.accent }}
            onChange={e => { const on = e.target.checked; setForm(p => { const c: GovConditions = { ...(p.conditions || {}) }; if (on) { c.form = 'consult'; c.form_by = 'manual'; } else { delete c.form; c.form_by = 'manual'; } return { ...p, conditions: c }; }); }} />
          컨설팅 · 교육만 <span style={{ color: '#9CA3AF' }}>(추천 안 함)</span>
          {form.conditions?.form === 'consult' && form.conditions?.form_by && form.conditions.form_by !== 'manual' && <span style={{ color: '#9CA3AF' }}>· {form.conditions.form_by === 'ai' ? '자동' : '리포트에서 뺌'}</span>}
        </label>
      </div>

      {/* 창업기업 여부 */}
      <div style={{ marginBottom: 12 }}>
        <div style={rowLabel}>창업기업 여부</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {['', '창업기업만', '비창업만'].map(opt => (
            <Pill key={opt || '무관'} active={form.startup_filter === opt} onClick={() => upd('startup_filter', opt)} activeBorder="#85B7EB" activeBg="#E6F1FB" activeColor="#0C447C">{opt || '무관'}</Pill>
          ))}
        </div>
      </div>

      {/* 대표 만나이 — 상한(청년) · 하한(시니어) 둘 다 지원(감사 A-6) */}
      <div style={{ marginBottom: 12 }}>
        <div style={rowLabel}>대표 만나이 <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(비우면 무관)</span></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: C.sub }}>만</span>
          <input type="number" min={0} max={120} value={form.ceo_age_min} onChange={e => upd('ceo_age_min', e.target.value)} placeholder="하한" style={numInput} />
          <span style={{ fontSize: 11, color: C.sub }}>~</span>
          <input type="number" min={0} max={120} value={form.ceo_age_max} onChange={e => upd('ceo_age_max', e.target.value)} placeholder="상한" style={numInput} />
          <span style={{ fontSize: 11, color: C.sub }}>세</span>
          {['39', '34', ''].map(v => (
            <Pill key={v || 'none'} active={form.ceo_age_max === v} onClick={() => upd('ceo_age_max', v)} activeBorder="#85B7EB" activeBg="#E6F1FB" activeColor="#0C447C">{v ? `상한 ${v}세` : '상한 무관'}</Pill>
          ))}
        </div>
      </div>

      {/* 매칭 조건 더보기 — fphub 카탈로그와 같은 세부 조건(연매출·상시근로자·업력·특허/상표). 접이식(감사 A-7) */}
      <div style={{ marginBottom: 12, border: `1px solid ${C.border}`, borderRadius: R.md, overflow: 'hidden' }}>
        <div onClick={() => setAdvOpen(o => !o)}
          style={{ padding: '9px 12px', background: C.bgAlt, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: C.sub }}>매칭 조건 더보기 <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(연매출·상시근로자·업력·특허/상표·추가 조건)</span>
            {!advOpen && condN > 0 && <span style={{ fontWeight: 600, color: C.accent }}> · 추가 조건 {condN}</span>}</span>
          <span style={{ fontSize: 11, color: C.muted }}>{advOpen ? '▾ 접기' : '▸ 펼치기'}</span>
        </div>
        {advOpen && (
          <div style={{ padding: 12 }}>
            <div style={{ marginBottom: 10 }}>
              <div style={rowLabel}>연매출 조건</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <select value={form.sales_op} onChange={e => upd('sales_op', e.target.value)} style={selInput}>
                  {OP_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                </select>
                <input type="number" min={0} step="0.1" value={form.sales_eok} onChange={e => upd('sales_eok', e.target.value)} placeholder="예: 20" style={numInput} />
                <span style={{ fontSize: 11, color: C.sub }}>억원</span>
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={rowLabel}>상시근로자 조건</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <select value={form.emp_op} onChange={e => upd('emp_op', e.target.value)} style={selInput}>
                  {OP_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                </select>
                <input type="number" min={0} value={form.emp_n} onChange={e => upd('emp_n', e.target.value)} placeholder="예: 5" style={numInput} />
                <span style={{ fontSize: 11, color: C.sub }}>명</span>
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={rowLabel}>업력 조건 <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(설립일 기준, 년)</span></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, color: C.sub }}>최소</span>
                <input type="number" min={0} step="0.5" value={form.yrs_min} onChange={e => upd('yrs_min', e.target.value)} placeholder="0" style={numInput} />
                <span style={{ fontSize: 11, color: C.sub }}>~ 최대</span>
                <input type="number" min={0} step="0.5" value={form.yrs_max} onChange={e => upd('yrs_max', e.target.value)} placeholder="7" style={numInput} />
                <span style={{ fontSize: 11, color: C.sub }}>년</span>
              </div>
            </div>
            <div>
              <div style={rowLabel}>가점 항목 <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(필수 아님, 있으면 우선순위만 올린다)</span></div>
              <div style={{ display: 'flex', gap: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 12, color: C.sub }}>
                  <input type="checkbox" checked={form.want_patent} onChange={e => upd('want_patent', e.target.checked)} style={{ accentColor: C.accent }} />특허 보유 권장
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 12, color: C.sub }}>
                  <input type="checkbox" checked={form.want_tm} onChange={e => upd('want_tm', e.target.checked)} style={{ accentColor: C.accent }} />상표 보유 권장
                </label>
              </div>
            </div>
            {/* 추가 조건 (2026-10-05) — 위 칸으로 못 담는 신청 자격 · FP 확인 항목. 대부분 공고 검토함 AI 가 채운 것을 확인만 */}
            <div style={{ marginTop: 10 }}>
              <div style={rowLabel}>추가 조건 <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(업종·지역 제외, 매출 감소, 수출, 개업일 — 칩을 누르면 고치기)</span></div>
              <ConditionsRow conditions={form.conditions} onChange={c => upd('conditions', c)} bizOptions={BIZ_TYPES} regionOptions={REGIONS} bizChosen={form.biz_types.length > 0} />
            </div>
          </div>
        )}
      </div>

      <div style={{ height: 1, background: C.border, margin: '12px 0' }} />

      {/* 분류 — 지도 체계(대분류 5 + 세부 15 · 정책자금 4, 「기타 › 기업 인증」 2026-10-01). 애매하면 대분류만 (지도사 2026-09-25) */}
      <div style={{ marginBottom: 12 }}>
        <div style={rowLabel}>분류 <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(복수 선택 · 세부가 애매하면 대분류만 체크)</span></div>
        <GovCategoryPicker
          value={(form.category ? form.category.split(',').map(s => s.trim()).filter(Boolean) : []).filter(c => !GOV_SPECIAL.includes(c))}
          onChange={(next) => {
            const arts = (form.category ? form.category.split(',').map(s => s.trim()) : []).filter(c => GOV_SPECIAL.includes(c)); // 전용 분야·기업 유형은 그대로 둔다
            upd('category', [...next, ...arts].join(','));
          }} />
      </div>

      {/* 전용 대상 — 기업 유형(분류 칸) + 특수 자격(conditions.sec) — 업종으로 알 수 없는 신분 · 자격만 (2026-10-05g). 자동으로 켠 것은 「자동」 표시 */}
      <GovTargetPicker
        category={form.category ? form.category.split(',').map(s => s.trim()).filter(Boolean) : []}
        onCategory={next => upd('category', next.join(','))}
        conditions={form.conditions}
        onConditions={c => upd('conditions', c)}
        nameAuto={nameAuto} />

      <Field label="공고 링크" value={form.link} placeholder="https://..." onChange={v => upd('link', v)} />

      {/* 세부 유형 (2026-10-05) — 대상·금액이 다른 유형이 있을 때만. 접혀 있고, 있으면 펼친 채로 */}
      <GovTracksSection tracks={form.tracks} onChange={ts => upd('tracks', ts)} bizOptions={BIZ_TYPES} regionOptions={REGIONS} />

      {/* 미리보기 — 「이 조건이면 우리 고객사 중 해당 N곳」 (600ms 디바운스, 실패해도 폼은 그대로) */}
      <GovPreviewLine payload={govProgramFormToPayload(form)} onResult={r => setNameAuto(prev => { const n = (r.targets || []).filter(t => t.from === 'title').map(t => t.k); return n.join('|') === prev.join('|') ? prev : n; })} />
    </>
  );
}
